import React, { useState, useEffect } from 'react';
import SignupForm from './SignupForm';
import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { auth } from '../../../shared/config/firebase';
import { useNavigate, useLocation } from 'react-router-dom'; 
import axiosInstance from '../../../shared/services/api/axiosInstance';
import preloadData from '../../../shared/utils/preloadService';
import { motion } from 'framer-motion';
import LiquidFillLoader from '../../../shared/components/ui/LiquidFillLoader';

// Hexagon component for blockchain design
const Hexagon = ({ size = 60, color = '#FFC9A4', className = '', children, ...props }) => {
  const width = size;
  const height = size * 1.1547; // Hexagon height ratio
  
  return (
    <div 
      className={`relative ${className}`}
      style={{ width, height }}
      {...props}
    >
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        <polygon
          points={`${width/2},0 ${width},${height*0.25} ${width},${height*0.75} ${width/2},${height} 0,${height*0.75} 0,${height*0.25}`}
          fill="none"
          stroke={color}
          strokeWidth="2"
        />
      </svg>
      {children && (
        <div className="absolute inset-0 flex items-center justify-center text-white text-xs font-bold">
          {children}
        </div>
      )}
    </div>
  );
};

// Hexagon Grid Component for seamless pattern
const HexagonGrid = ({ side = 'left', color = '#FFC9A4' }) => {
  const hexSize = 30;
  const hexWidth = hexSize;
  const hexHeight = hexSize * 1.1547;
  const rows = Math.ceil((typeof window !== 'undefined' ? window.innerHeight : 800) / hexHeight) + 2;
  const cols = Math.ceil(350 / hexWidth) + 2; // 350px width for extended side panels
  
  const hexagons = [];
  
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const x = col * hexWidth * 0.75;
      const y = row * hexHeight + (col % 2) * (hexHeight / 2);
      
      hexagons.push(
        <motion.div
          key={`${side}-${row}-${col}`}
          className="absolute"
          style={{
            left: x,
            top: y,
            width: hexWidth,
            height: hexHeight
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.3 }}
          transition={{ 
            duration: 0.5, 
            delay: (row + col) * 0.02,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        >
          <svg width={hexWidth} height={hexHeight} viewBox={`0 0 ${hexWidth} ${hexHeight}`}>
            <polygon
              points={`${hexWidth/2},0 ${hexWidth},${hexHeight*0.25} ${hexWidth},${hexHeight*0.75} ${hexWidth/2},${hexHeight} 0,${hexHeight*0.75} 0,${hexHeight*0.25}`}
              fill="none"
              stroke={color}
              strokeWidth="1"
            />
          </svg>
        </motion.div>
      );
    }
  }
  
  return (
    <div 
      className={`absolute top-0 h-full w-[350px] overflow-hidden ${side === 'left' ? 'left-0' : 'right-0'} hidden md:block`}
      style={{ zIndex: 5 }}
    >
      {hexagons}
    </div>
  );
};

// Blockchain block component
const BlockchainBlock = ({ className = '', ...props }) => {
  return (
    <motion.div 
      className={`relative ${className}`}
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ scale: 1.05 }}
      {...props}
    >
      <div className="border-2 border-[#FFC9A4] bg-transparent p-3 rounded-lg shadow-lg">
      </div>
    </motion.div>
  );
};

function Interface() {
  const [loading, setLoading] = useState(false);
  const [showLogin, setShowLogin] = useState(true);
  const [a, seta] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  // Effect to handle routing based on current path
  useEffect(() => {
    if (location.pathname === '/signup') {
      setShowLogin(false);
      seta(1);
    } else {
      // Default to login page
      setShowLogin(true);
      seta(0);
      navigate('/login');
    }
  }, [location.pathname, navigate]);

  const toggleLoading = (isLoading) => {
    setLoading(isLoading !== undefined ? isLoading : !loading);
  };

  const toggleForm = () => {
    if (showLogin) {
      // Switch to signup route
      navigate('/signup');
    } else {
      // Switch to login route
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen w-full overflow-hidden bg-[#f8fafc] relative" style={{ fontFamily: 'Raleway, sans-serif' }}>
      {/* Animated Background with Modern Design */}
      <div className="absolute inset-0 z-0">
        {/* Radial gradient: edges 8979FF → FFC9A4 → white (darker at sides) */}
        <div
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse 85% 85% at 50% 50%, #ffffff 0%, rgba(255,255,255,0.97) 28%, rgba(255,201,164,0.2) 52%, rgba(137,121,255,0.25) 78%, rgba(137,121,255,0.3) 100%)',
          }}
        />
        
        {/* Animated floating orbs */}
        <motion.div
          className="absolute top-20 left-10 w-72 h-72 bg-[#8979FF]/10 rounded-full blur-3xl"
          animate={{
            x: [0, 50, 0],
            y: [0, 30, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute bottom-20 right-10 w-96 h-96 bg-[#FFC9A4]/10 rounded-full blur-3xl"
          animate={{
            x: [0, -40, 0],
            y: [0, -50, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute top-1/2 left-1/2 w-80 h-80 bg-[#8979FF]/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"
          animate={{
            scale: [1, 1.15, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        
        {/* Subtle dot pattern overlay */}
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: 'radial-gradient(circle, #8979FF 1px, transparent 1px)',
            backgroundSize: '40px 40px',
            backgroundPosition: '0 0',
          }}
        />
          
          












        </div>

      {/* Logo */}
      <div className="absolute top-6 left-6 z-20">
        <img 
          src="/login-logo.png" 
          alt="Cryptique Logo" 
          className="h-10 w-auto"
        />
      </div>

      {/* Login Form */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <motion.div 
          className="w-full max-w-xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          style={{ zIndex: 20 }}
        >
          <div className="text-center mb-8">
            <h1 
              className="text-4xl mb-2 text-[#0f172a]" 
              style={{ fontFamily: 'Raleway, sans-serif', fontWeight: '500' }}
            >
              Welcome to Cryptique
            </h1>
          </div>

          {showLogin ? (
            <LoginForm onSignupClick={toggleForm} toggleLoading={toggleLoading}/>
          ) : (
            <SignupForm onBackToLogin={toggleForm} />
          )}
        </motion.div>
      </div>

      {loading && (
        <div className="fixed top-0 left-0 w-full h-full bg-black/80 z-50 flex items-center justify-center">
          <LiquidFillLoader size={120} />
        </div>
      )}
    </div>
  );
}

function LoginForm({ onSignupClick, toggleLoading }) {
  const [step, setStep] = useState(1); // 1 = email step, 2 = password step
  const [email, setEmail] = useState('');
  const [savedEmail, setSavedEmail] = useState(''); // Store email when moving to step 2
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState('');
  const navigate = useNavigate();
  
  useEffect(() => {
    if (step === 1) {
      setIsFormValid(email.trim() !== '' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
    } else {
      setIsFormValid(password.trim() !== '');
    }
  }, [email, password, step]);
  
  const handleContinue = (e) => {
    e.preventDefault();
    if (email.trim() !== '' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setSavedEmail(email);
      setStep(2);
    }
  };
  
  const handleBackToEmail = () => {
    setStep(1);
    setPassword('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      toggleLoading(true);
      const response = await axiosInstance.post('/auth/login', {
        email: savedEmail, 
        password 
      });
      
      if (response.data.user) {
        localStorage.setItem('accessToken', response.data.accessToken);
        localStorage.setItem('User', JSON.stringify(response.data.user));
        
        // Use the default team from server response or fall back to username
        const defaultTeam = response.data.defaultTeam;
        if (defaultTeam) {
          localStorage.setItem('selectedTeam', defaultTeam.name);
          localStorage.setItem('selectedTeamData', JSON.stringify(defaultTeam));
          console.log('Using user\'s default team:', defaultTeam.name);
          
          // Fetch and cache subscription data for the team
          try {
            const subscriptionResponse = await axiosInstance.get(`/api/billing/status/${defaultTeam._id}`);
            if (subscriptionResponse.data && subscriptionResponse.data.success && subscriptionResponse.data.data) {
              const subscriptionData = subscriptionResponse.data.data;
              const { plan, status, hasCQIntelligence, isActive, limits, accessiblePages } = subscriptionData;
              
              // Update team data with subscription info
              const updatedTeamData = {
                ...defaultTeam,
                subscription: {
                  plan: plan || 'free',
                  status: status || 'incomplete',
                  hasCQIntelligence: hasCQIntelligence || false
                }
              };
              localStorage.setItem('selectedTeamData', JSON.stringify(updatedTeamData));
              
              // Cache subscription status separately for faster access using centralized data
              const subscriptionCache = {
                isActive: isActive || false,
                plan: plan || 'free',
                status: status || 'incomplete',
                hasCQIntelligence: hasCQIntelligence || false,
                limits: limits || {},
                accessiblePages: accessiblePages || ['dashboard', 'settings'],
                timestamp: Date.now()
              };
              
              // Store in sessionStorage (persists for current session)
              sessionStorage.setItem('subscriptionStatus', JSON.stringify(subscriptionCache));
              
              // Also store in localStorage (for session recovery)
              localStorage.setItem('subscriptionStatus', JSON.stringify(subscriptionCache));
              
              console.log('Subscription data cached during login:', subscriptionCache);
              
              // Dispatch event to notify subscription context of the update
              window.dispatchEvent(new CustomEvent('teamDataUpdated', { 
                detail: { teamData: updatedTeamData }
              }));
            }
          } catch (subscriptionError) {
            console.error('Error fetching subscription data during login:', subscriptionError);
            // Continue with login even if subscription fetch fails
          }
        } else {
          // Fallback to old behavior if no default team
          const fallbackTeam = savedEmail.split('@')[0];
          localStorage.setItem('selectedTeam', fallbackTeam);
          console.log('No default team found, using fallback:', fallbackTeam);
        }
        
        // Preload data before navigating to dashboard
        await preloadData();
        
        toggleLoading(false);
        
        // Force refresh to ensure subscription context is properly initialized
        // This prevents the "Upgrade Now" screen from showing for premium users
        console.log('Login successful, refreshing page to ensure proper subscription context initialization');
        window.location.href = '/dashboard';
      } else {
        toggleLoading(false);
        alert(response.data.message || 'Invalid credentials');
      }
    } catch (error) {
      toggleLoading(false);
      
      // Handle unverified user case (status 402 with requiresVerification)
      if (error.response && error.response.status === 402 && error.response.data?.requiresVerification) {
        // User exists and password is correct, but not verified
        // Backend has sent a new OTP
        if (error.response.data.accessToken) {
          localStorage.setItem('accessToken', error.response.data.accessToken);
        }
        if (error.response.data.user) {
          localStorage.setItem('User', JSON.stringify(error.response.data.user));
        }
        
        // Show message and redirect to signup page with OTP view
        // Or we could add OTP view to login form, but for now redirect to signup
        alert(error.response.data.message || 'A new OTP has been sent to your email. Please verify to complete login.');
        
        // Redirect to signup page - it will handle OTP verification
        // The signup form will detect existing user in localStorage and show OTP view
        window.location.href = '/signup?email=' + encodeURIComponent(savedEmail);
        return;
      }
      
      if (error.response && 
          [401, 404, 402].includes(error.response.status)) {
        alert(error.response.data.message);
      } else {
        console.error('Error during login:', error);
        alert('An error occurred. Please try again.');
      }
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleResetPassword = async () => {
    if (!savedEmail.trim()) {
      setForgotPasswordMessage('Please enter your email address');
      return;
    }

    try {
      const response = await axiosInstance.post('/auth/forgot-password', {
        email: savedEmail
      });
      
      setForgotPasswordMessage('Password reset link has been sent to your email');
      setTimeout(() => {
        setForgotPasswordMessage('');
      }, 3000);
    } catch (error) {
      console.error('Error sending reset email:', error);
      setForgotPasswordMessage(
        error.response?.data?.message || 'Error sending reset email. Please try again.'
      );
    }
  };

  const googleLogin = async () => {
    const provider = new GoogleAuthProvider();
    
    // Add custom parameters for better security perception
    provider.setCustomParameters({
      prompt: 'select_account',
      login_hint: '',
      // Use app.cryptique.io domain for auth callback
      state: encodeURIComponent(JSON.stringify({origin: window.location.origin}))
    });
    
    let user = null; // Declare user variable outside try block
    
    try {
      toggleLoading(true);
      const result = await signInWithPopup(auth, provider);
      user = result.user;
      
      // Handle successful login
      const response = await axiosInstance.post('/auth/google-login', {
        name: user.displayName,
        email: user.email,
        auth_provider_info: user.photoURL, // Google profile URL for auth provider info
      });
      
      if (response.data.user) {
        localStorage.setItem('accessToken', response.data.accessToken);
        localStorage.setItem('User', JSON.stringify(response.data.user));
        
        // Use the default team from server response or fall back to username
        const defaultTeam = response.data.defaultTeam;
        if (defaultTeam) {
          localStorage.setItem('selectedTeam', defaultTeam.name);
          localStorage.setItem('selectedTeamData', JSON.stringify(defaultTeam));
          console.log('Using user\'s default team from Google login:', defaultTeam.name);
          
          // Fetch and cache subscription data for the team
          try {
            const subscriptionResponse = await axiosInstance.get(`/api/billing/status/${defaultTeam._id}`);
            if (subscriptionResponse.data && subscriptionResponse.data.success && subscriptionResponse.data.data) {
              const subscriptionData = subscriptionResponse.data.data;
              const { plan, status, hasCQIntelligence, isActive, limits, accessiblePages } = subscriptionData;
              
              // Update team data with subscription info
              const updatedTeamData = {
                ...defaultTeam,
                subscription: {
                  plan: plan || 'free',
                  status: status || 'incomplete',
                  hasCQIntelligence: hasCQIntelligence || false
                }
              };
              localStorage.setItem('selectedTeamData', JSON.stringify(updatedTeamData));
              
              // Cache subscription status separately for faster access using centralized data
              const subscriptionCache = {
                isActive: isActive || false,
                plan: plan || 'free',
                status: status || 'incomplete',
                hasCQIntelligence: hasCQIntelligence || false,
                limits: limits || {},
                accessiblePages: accessiblePages || ['dashboard', 'settings'],
                timestamp: Date.now()
              };
              
              // Store in sessionStorage (persists for current session)
              sessionStorage.setItem('subscriptionStatus', JSON.stringify(subscriptionCache));
              
              // Also store in localStorage (for session recovery)
              localStorage.setItem('subscriptionStatus', JSON.stringify(subscriptionCache));
              
              console.log('Subscription data cached during Google login:', subscriptionCache);
              
              // Dispatch event to notify subscription context of the update
              window.dispatchEvent(new CustomEvent('teamDataUpdated', { 
                detail: { teamData: updatedTeamData }
              }));
            }
          } catch (subscriptionError) {
            console.error('Error fetching subscription data during Google login:', subscriptionError);
            // Continue with login even if subscription fetch fails
          }
        } else {
          // Fallback to old behavior if no default team
          const fallbackTeam = user.email.split('@')[0];
          localStorage.setItem('selectedTeam', fallbackTeam);
          console.log('No default team found in Google login, using fallback:', fallbackTeam);
        }
        
        // Preload data before navigating to dashboard
        await preloadData();
        
        toggleLoading(false);
        
        // Force refresh to ensure subscription context is properly initialized
        // This prevents the "Upgrade Now" screen from showing for premium users
        console.log('Google login successful, refreshing page to ensure proper subscription context initialization');
        window.location.href = '/dashboard';
      } else {
        toggleLoading(false);
        alert(response.data.message || 'Login failed');
      }
    } catch (error) {
      toggleLoading(false);
      console.error('Error during Google login:', error);
      alert('Google login failed. Please try again.');
    }
  };

  // Step 1: Email entry
  if (step === 1) {
  return (
    <div className="space-y-6">
        <form onSubmit={handleContinue} className="space-y-4">
          <div className="relative">
            <input 
              type="email" 
              id="email" 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 bg-white border border-[#DDE2EE] rounded-lg text-[#0f172a] placeholder-[#475569] focus:outline-none focus:ring-2 focus:ring-[#8979FF]/20 focus:border-[#8979FF]"
              placeholder="Email address (Required)"
              required
              style={{ fontFamily: 'Raleway, sans-serif' }}
            />
          </div>

          <button 
            type="submit" 
            className={`w-full py-3 rounded-lg font-medium transition-colors ${
              isFormValid 
                ? 'bg-[#8979FF] text-white hover:bg-[#7c6ae8]' 
                : 'bg-[#DDE2EE] text-[#475569] cursor-not-allowed'
            }`}
            disabled={!isFormValid}
            style={{ fontFamily: 'Raleway, sans-serif' }}
          >
            Continue
          </button>
        </form>

        {/* Divider */}
        <div className="flex items-center">
          <div className="flex-grow h-px bg-[#DDE2EE]"></div>
          <div className="px-4 text-sm text-[#475569]" style={{ fontFamily: 'Raleway, sans-serif' }}>OR</div>
          <div className="flex-grow h-px bg-[#DDE2EE]"></div>
        </div>

        {/* Google Sign in */}
        <button
          onClick={googleLogin} 
          className="w-full py-3 px-4 bg-white hover:bg-gray-50 border border-[#DDE2EE] rounded-lg flex items-center justify-center gap-3 transition-colors text-[#0f172a] shadow-sm"
          style={{ fontFamily: 'Raleway, sans-serif' }}
        >
          <svg width="20" height="20" viewBox="0 0 20 20">
            <path d="M19.8 10.2c0-.7-.1-1.4-.2-2h-9.6v3.8h5.5c-.2 1.2-1 2.3-2.1 3v2.5h3.4c2-1.8 3-4.5 3-7.3z" fill="#4285F4"/>
            <path d="M10 20c2.9 0 5.3-1 7-2.6l-3.4-2.6c-.9.6-2.1 1-3.6 1-2.8 0-5.1-1.9-6-4.4H.5v2.7c1.8 3.5 5.4 5.9 9.5 5.9z" fill="#34A853"/>
            <path d="M4 10.2c-.2-.6-.4-1.3-.4-2s.1-1.4.4-2V3.5H.5c-.8 1.6-1.3 3.4-1.3 5.2 0 1.8.5 3.6 1.3 5.2l3.5-2.7z" fill="#FBBC05"/>
            <path d="M10 3.9c1.6 0 3 .5 4.1 1.6l3-3C15.3.9 12.9 0 10 0 5.9 0 2.3 2.4.5 5.9l3.5 2.7c.8-2.5 3.2-4.7 6-4.7z" fill="#EA4335"/>
          </svg>
          <span>Sign in with Google</span>
        </button>

        <div className="text-center text-sm text-[#475569]" style={{ fontFamily: 'Raleway, sans-serif' }}>
          <p>
            Looking to get started with Cryptique for your business?{' '}
      <button 
              onClick={onSignupClick} 
              className="text-[#8979FF] hover:text-[#7c6ae8] font-medium inline-flex items-center gap-1"
      >
              Sign up
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="inline">
                <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
          </p>
        </div>
      </div>
    );
  }

  // Step 2: Password entry
  return (
    <div className="space-y-6">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Email field (read-only) */}
        <div>
          <label htmlFor="email-display" className="block mb-2 text-sm text-[#475569]" style={{ fontFamily: 'Raleway, sans-serif' }}>
            Email address
          </label>
          <input 
            type="email" 
            id="email-display" 
            value={savedEmail}
            readOnly
            className="w-full px-4 py-3 bg-[#f8fafc] border border-[#DDE2EE] rounded-lg text-[#0f172a] cursor-not-allowed"
            style={{ fontFamily: 'Raleway, sans-serif' }}
          />
        </div>

        {/* Password field */}
        <div>
          <label htmlFor="password" className="block mb-2 text-sm text-[#475569]" style={{ fontFamily: 'Raleway, sans-serif' }}>
              Password
            </label>
          <div className="relative">
            <input 
              type={showPassword ? "text" : "password"} 
              id="password" 
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 bg-white border border-[#DDE2EE] rounded-lg text-[#0f172a] placeholder-[#475569] focus:outline-none focus:ring-2 focus:ring-[#8979FF]/20 focus:border-[#8979FF] pr-12"
              style={{ fontFamily: 'Raleway, sans-serif' }}
              placeholder="Enter your password"
              required
              autoFocus
            />
            <button 
              type="button" 
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-[#475569] hover:text-[#8979FF]"
              onClick={togglePasswordVisibility}
            >
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center justify-between gap-3">
          <button 
            type="button" 
            onClick={handleBackToEmail}
            className="text-sm text-[#475569] hover:text-[#8979FF] transition-colors"
            style={{ fontFamily: 'Raleway, sans-serif' }}
          >
            ← Use a different email
          </button>
          <button 
            type="button" 
            onClick={handleResetPassword}
            className="text-sm text-[#475569] hover:text-[#8979FF] transition-colors"
            style={{ fontFamily: 'Raleway, sans-serif' }}
          >
            Reset password
          </button>
        </div>

        {/* Reset password message */}
        {forgotPasswordMessage && (
          <div className={`p-3 rounded-lg text-sm ${
            forgotPasswordMessage.includes('sent') 
              ? 'bg-green-50 text-green-700 border border-green-200' 
              : 'bg-red-50 text-red-700 border border-red-200'
          }`} style={{ fontFamily: 'Raleway, sans-serif' }}>
            {forgotPasswordMessage}
          </div>
        )}

        {/* Sign in button */}
        <button 
          type="submit" 
          className={`w-full py-3 rounded-lg font-medium transition-colors ${
            isFormValid 
              ? 'bg-[#8979FF] text-white hover:bg-[#7c6ae8]' 
              : 'bg-[#DDE2EE] text-[#475569] cursor-not-allowed'
          }`}
          disabled={!isFormValid}
          style={{ fontFamily: 'Raleway, sans-serif' }}
        >
          Sign in to Cryptique
        </button>
      </form>

      {/* Divider */}
      <div className="flex items-center">
        <div className="flex-grow h-px bg-[#DDE2EE]"></div>
        <div className="px-4 text-sm text-[#475569]" style={{ fontFamily: 'Raleway, sans-serif' }}>OR</div>
        <div className="flex-grow h-px bg-[#DDE2EE]"></div>
      </div>

      {/* Google Sign in */}
                <button
        onClick={googleLogin} 
        className="w-full py-3 px-4 bg-white hover:bg-gray-50 border border-[#DDE2EE] rounded-lg flex items-center justify-center gap-3 transition-colors text-[#0f172a] shadow-sm"
        style={{ fontFamily: 'Raleway, sans-serif' }}
      >
        <svg width="20" height="20" viewBox="0 0 20 20">
          <path d="M19.8 10.2c0-.7-.1-1.4-.2-2h-9.6v3.8h5.5c-.2 1.2-1 2.3-2.1 3v2.5h3.4c2-1.8 3-4.5 3-7.3z" fill="#4285F4"/>
          <path d="M10 20c2.9 0 5.3-1 7-2.6l-3.4-2.6c-.9.6-2.1 1-3.6 1-2.8 0-5.1-1.9-6-4.4H.5v2.7c1.8 3.5 5.4 5.9 9.5 5.9z" fill="#34A853"/>
          <path d="M4 10.2c-.2-.6-.4-1.3-.4-2s.1-1.4.4-2V3.5H.5c-.8 1.6-1.3 3.4-1.3 5.2 0 1.8.5 3.6 1.3 5.2l3.5-2.7z" fill="#FBBC05"/>
          <path d="M10 3.9c1.6 0 3 .5 4.1 1.6l3-3C15.3.9 12.9 0 10 0 5.9 0 2.3 2.4.5 5.9l3.5 2.7c.8-2.5 3.2-4.7 6-4.7z" fill="#EA4335"/>
        </svg>
        <span>Sign in with Google</span>
                </button>
    </div>
  );
}

export default Interface;